import { AlertCircleIcon } from '@langchain/untitled-ui-icons';

import { Text, Tooltip } from '@/design-system';
import { FeedbackStatsFieldValues } from '@/types/schema';
import { getColorByString } from '@/utils/get-color-by-string';

interface TotalFeedbackBarProps {
  type: string;
  feedbackStats: FeedbackStatsFieldValues;
  minMax?: [number, number];
}

const FeedbackChip = ({
  type,
  children,
  className = '',
}: {
  type: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`inline-flex h-5 items-center justify-start gap-1 rounded border border-secondary px-1 text-center text-xs font-medium leading-none ${className}`}
    data-testid={`feedback-stats-${type}-chip`}
  >
    {children}
  </div>
);

const FeedbackValuesDisplay = ({
  feedbackValues,
  type,
}: {
  feedbackValues: Record<string, number>;
  type: string;
}) => (
  <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden no-scrollbar">
    {Object.entries(feedbackValues)
      .sort(([, a], [, b]) => b - a)
      .map(([key, count], index) => (
        <FeedbackChip key={index} type={type} className="shrink-0">
          <span className="line-clamp-1 max-w-[100px] whitespace-normal break-all">
            {key}
          </span>
          <span className="tabular-nums">({count})</span>
        </FeedbackChip>
      ))}
  </div>
);

const AvgFeedbackDisplay = ({
  avgFeedback,
  stdevFeedback,
  ratioAvg,
  type,
}: {
  avgFeedback: number;
  stdevFeedback?: number;
  ratioAvg: number;
  type: string;
}) => (
  <Tooltip title={`σ = ${stdevFeedback?.toFixed(2) ?? 'N/A'}`}>
    <div>
      <FeedbackChip type={type} className="text-tertiary">
        <span className="tabular-nums">{avgFeedback?.toFixed(2)}</span>
        <div className="h-1 w-10 overflow-hidden rounded-md bg-secondary">
          <div
            className="h-1"
            style={{
              width: `${ratioAvg * 100}%`,
              background: getColorByString(type),
            }}
          />
        </div>
      </FeedbackChip>
    </div>
  </Tooltip>
);

const ErrorIndicator = ({ errorRate }: { errorRate: number }) => {
  if (errorRate === 0) return null;

  if (errorRate < 1) {
    return (
      <Tooltip title="Percent of evaluator runs that failed">
        <div className="inline-flex h-5 items-center justify-center gap-1 rounded px-1.5 text-error">
          <AlertCircleIcon className="h-4 w-4" />
          <Text variant="sm" weight="medium">
            {Math.round(errorRate * 100)}%
          </Text>
        </div>
      </Tooltip>
    );
  }

  return (
    <div className="inline-flex h-5 items-center justify-center gap-1 rounded px-1.5">
      <Text variant="sm" weight="medium" className="text-error">
        All Failed
      </Text>
    </div>
  );
};

function shouldShowComponent(
  avgFeedback: number | null | undefined,
  stdevFeedback: number | undefined,
  feedbackValues: Record<string, number> | undefined,
  feedbackErrors: number
): boolean {
  const feedbackHasNoValues =
    feedbackValues == null || Object.keys(feedbackValues).length === 0;

  // Return false if no valid data to show
  if (
    (avgFeedback == null || stdevFeedback == null) &&
    feedbackHasNoValues &&
    (feedbackErrors === undefined || feedbackErrors === 0)
  ) {
    return false;
  }

  if (avgFeedback == null && feedbackHasNoValues && feedbackErrors === 0) {
    return false;
  }

  return true;
}

export const TotalFeedbackBar = ({
  feedbackStats,
  type,
  minMax,
}: TotalFeedbackBarProps) => {
  const avgFeedback = feedbackStats?.avg ?? 0;
  const feedbackErrors = feedbackStats?.errors ?? 0;
  const stdevFeedback =
    feedbackStats?.stdev == null ? undefined : feedbackStats.stdev;
  const feedbackValues = feedbackStats?.values;

  const [minAvg, maxAvg] = minMax ?? [avgFeedback, avgFeedback];
  const ratioAvg =
    maxAvg !== minAvg ? (avgFeedback - minAvg) / (maxAvg - minAvg) : 0;
  const errorRate = feedbackStats?.n ? feedbackErrors / feedbackStats.n : 0;

  const feedbackHasNoValues =
    feedbackValues == null || Object.keys(feedbackValues).length === 0;

  if (
    !shouldShowComponent(
      feedbackStats?.avg,
      stdevFeedback,
      feedbackValues,
      feedbackErrors
    )
  ) {
    return null;
  }

  const shouldShowFeedback =
    !(feedbackStats?.avg == null && feedbackHasNoValues) && errorRate !== 1;

  return (
    <div className="flex items-center gap-2">
      {shouldShowFeedback &&
        (!feedbackHasNoValues ? (
          <FeedbackValuesDisplay feedbackValues={feedbackValues} type={type} />
        ) : (
          <AvgFeedbackDisplay
            avgFeedback={avgFeedback}
            stdevFeedback={stdevFeedback}
            ratioAvg={ratioAvg}
            type={type}
          />
        ))}
      <ErrorIndicator errorRate={errorRate} />
    </div>
  );
};
